import { VersionedEntitySelect } from '@/components/common/versioned-entity-select/VersionedEntitySelect';
import type { VersionedEntityAdapter } from '@/components/common/versioned-entity-select/types';
import { useListActionWorkflowOptions } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { useListWorkflowVersions } from 'src/api/rpc/hooks/workflows/useWorkflowVersioning';

/**
 * A stored reference to a reusable workflow used as an action.
 * `workflowDefinitionId === null` means "track the latest published version"
 * (resolved on the backend via the workflow's pinned version at run time).
 */
export interface WorkflowActionRef {
  workflowId: string;
  workflowDefinitionId: string | null;
}

/**
 * Build a {@link WorkflowActionRef} from a persisted action target, tolerating
 * legacy configs that stored only a bare `workflowDefinitionId`. For legacy
 * configs we recover the lineage id from the action options when the stored id
 * is still the pinned version (the common case); otherwise the selection can't
 * be rendered, but the stored config still resolves correctly at run time.
 */
export function deriveWorkflowActionRef(
  stored: { workflowId?: string | null; workflowDefinitionId?: string | null },
  workflows: { id: string; workflowId: string }[],
): WorkflowActionRef | null {
  const workflowId =
    typeof stored.workflowId === 'string' ? stored.workflowId : undefined;
  const workflowDefinitionId =
    typeof stored.workflowDefinitionId === 'string'
      ? stored.workflowDefinitionId
      : null;

  if (workflowId) return { workflowId, workflowDefinitionId };

  if (workflowDefinitionId) {
    const match = workflows.find((w) => w.id === workflowDefinitionId);
    if (match) {
      return { workflowId: match.workflowId, workflowDefinitionId };
    }
  }

  return null;
}

// Adapter that feeds the generic VersionedEntitySelect with workflow data. The
// lineage id is the workflow's `workflowId` (the action option's own `id` is the
// pinned definition id); workflow version summaries already carry `isPinned`.
function useWorkflowActionOptions() {
  const { data, isLoading, isError } = useListActionWorkflowOptions();
  const options = (data?.workflows ?? []).map((workflow) => ({
    id: workflow.workflowId,
    name: workflow.name,
  }));
  return { options, isLoading, isError };
}

function useWorkflowActionVersions(entityId: string | undefined) {
  const { data, isLoading, isError } = useListWorkflowVersions(entityId);
  const versions = (data?.versions ?? []).map((v) => ({
    id: v.id,
    version: v.version,
    status: v.status,
    isPinned: v.isPinned,
  }));
  return { versions, isLoading, isError };
}

const workflowActionAdapter: VersionedEntityAdapter = {
  useOptions: useWorkflowActionOptions,
  useVersions: useWorkflowActionVersions,
  copy: { entityNoun: 'reusable workflow' },
};

/**
 * Workflow-specific wrapper over {@link VersionedEntitySelect}. Keeps the
 * `{ workflowId, workflowDefinitionId }` shape its callsites persist, mapping
 * to/from the generic `{ entityId, versionId }` reference.
 */
export function WorkflowActionVersionSelect({
  value,
  onChange,
  disabled,
  allowClear,
}: {
  value: WorkflowActionRef | null;
  onChange: (value: WorkflowActionRef | null) => void;
  disabled?: boolean;
  allowClear?: boolean;
}) {
  return (
    <VersionedEntitySelect
      adapter={workflowActionAdapter}
      value={
        value
          ? {
              entityId: value.workflowId,
              versionId: value.workflowDefinitionId,
            }
          : null
      }
      onChange={(ref) =>
        onChange(
          ref
            ? { workflowId: ref.entityId, workflowDefinitionId: ref.versionId }
            : null,
        )
      }
      disabled={disabled}
      allowClear={allowClear}
    />
  );
}
